import { useSnack } from '@reapit/elements'
import { Configuration, Settings } from '@reapit-sales/api-v2'
import { SecondaryNavigationMenu, UseModalIframeType } from '@reapit-sales/navigation'
import { ThirdPartyExternalLink } from '@reapit-sales/legacy-crm'
import { generateZohoURL, ZOHO_MODAL_TITLE, ZohoRequestType } from '../utils/generate-zoho-url'
import { buildLitmosRedirectPageURL } from '@crm/components/litmos/buildLitmosRedirectPageURL'

import {
  liveChat,
  litmosSSO,
  featureToggle,
  openNewPage,
  trackMixpanelAction,
  NAVIGATION,
  handleLiveChatSupportClick,
} from '@reapit-sales/utilities'

import { HelpIcon } from '@reapit/elements/icons/help'
import { MessageIcon } from '@reapit/elements/icons/message'
import { ExportIcon } from '@reapit/elements/icons/export'
import { CopyIcon } from '@reapit/elements/icons/copy'

const SubmitSupportRequest: ZohoRequestType = 'SubmitSupportRequest'
const SystemChangeRequest: ZohoRequestType = 'SystemChangeRequest'

/**
 * `getHelpMenu` generates a "Help" menu with various submenu items for user support.
 *
 * Props:
 * @param userConfig (array): This is configuration information from APIv2 - GET/configurations.
 * @param modalController (UseModalIframeType): The modal controller to manage the visibility of modals.
 *
 * Returns:
 * (SecondaryNavigationMenu): A "Help" menu with several submenu items for support actions.
 *
 * This function:
 * 1. Creates a "Help" menu with a series of sub-menu items including:
 *    - "Start Support Chat" (no functionality yet).
 *    - "Submit Support Request" (opens a modal with the URL for the support request form).
 *    - "Submit System Change" (opens a modal for submitting system changes).
 *    - "Knowledge Base" (opens the knowledge base page).
 *    - "Training Webinars" (opens the training webinars page).
 *    - "Reapit Sales Academy" (opens the Reapit Academy page).
 *    - "System ID" (copies the system ID to the clipboard).
 * 2. Opens the appropriate page or modal when the user selects a submenu item.
 * 3. `hasAccess` is optional and only provide it if menu option require to be behind permission check
 */
export const getHelpMenu = (
  userConfig: Configuration,
  modalController: UseModalIframeType,
  legacyCRMIframePageName: string,
  settings: Settings,
): SecondaryNavigationMenu => {
  const { success, error } = useSnack()

  return {
    label: 'Help',
    icon: <HelpIcon />,
    onClick: () => {
      // Track the Mixpanel event for opening Help Menu
      trackMixpanelAction(NAVIGATION.ACTION.OPEN_HELP_MENU, {
        Menu: NAVIGATION.MENU_NAME.HELP_MENU,
        FromRoute: legacyCRMIframePageName,
      })
    },
    subMenu: [
      {
        label: 'group-communication',
        displayAsGroup: featureToggle(liveChat, settings),
        group: [
          {
            label: 'Start Support Chat',
            hasAccess: featureToggle(liveChat, settings),
            rightIcon: <MessageIcon />,
            onClick: handleLiveChatSupportClick(legacyCRMIframePageName),
          },
        ],
      },
      {
        label: 'group-support-requests',
        group: [
          {
            label: 'Submit Support Request',
            onClick: () => {
              trackMixpanelAction(NAVIGATION.ACTION.NAVIGATE_SUBMIT_SUPPORT_REQUEST, {
                Modal: NAVIGATION.MODAL_NAME.SUBMIT_SUPPORT_REQUEST,
                FromRoute: legacyCRMIframePageName,
              })
              modalController.enableModalVisibility(
                ZOHO_MODAL_TITLE[SubmitSupportRequest],
                generateZohoURL(SubmitSupportRequest, userConfig),
              )
            },
          },
          {
            label: 'Submit System Change',
            hasAccess: userConfig.privileges?.changeRequest?.canAccess,
            onClick: () => {
              trackMixpanelAction(NAVIGATION.ACTION.NAVIGATE_SUBMIT_SYSTEM_CHANGE, {
                Modal: NAVIGATION.MODAL_NAME.SUBMIT_SYSTEM_CHANGE,
                FromRoute: legacyCRMIframePageName,
              })
              modalController.enableModalVisibility(
                ZOHO_MODAL_TITLE[SystemChangeRequest],
                generateZohoURL(SystemChangeRequest, userConfig),
              )
            },
          },
        ],
      },
      {
        label: 'group-knowledge-base',
        group: [
          {
            label: 'Knowledge Base',
            rightIcon: <ExportIcon />,
            onClick: openNewPage(
              userConfig.settings?.externalResources?.helpdocs.url,
              NAVIGATION.ACTION.NAVIGATE_KNOWLEDGE_BASE,
              legacyCRMIframePageName,
            ),
          },
          {
            label: 'Training Webinars',
            rightIcon: <ExportIcon />,
            onClick: openNewPage(
              userConfig.settings?.externalResources?.trainingWebinars.url,
              NAVIGATION.ACTION.NAVIGATE_TRAINING_WEBINARS,
              legacyCRMIframePageName,
            ),
          },
          {
            label: 'Reapit Sales Academy',
            rightIcon: <ExportIcon />,
            // Checking if the feature toggle is enabled then redirect to litmos route else open the default link
            onClick: featureToggle(litmosSSO, settings)
              ? openNewPage(buildLitmosRedirectPageURL())
              : openNewPage(
                  ThirdPartyExternalLink.ReapitAcademy.url,
                  NAVIGATION.ACTION.NAVIGATE_REAPIT_SALES_ACADEMY,
                  legacyCRMIframePageName,
                ),
          },
        ],
      },
      {
        label: 'group-system-information',
        group: [
          {
            label: `System ID: ${userConfig.settings?.system?.abmsId || 'Unavailable'}`,
            rightIcon: <CopyIcon />,
            color: 'secondary',
            onClick: async () => {
              try {
                if (!userConfig.settings?.system?.abmsId) {
                  throw new Error('System ID is unavailable')
                }

                await navigator.clipboard.writeText(userConfig.settings?.system?.abmsId)

                success('System ID copied to clipboard')
              } catch (err) {
                error('Failed to copy System ID: ' + err)
              }
              trackMixpanelAction(NAVIGATION.ACTION.NAVIGATE_SYSTEM_ID, {
                Modal: NAVIGATION.MODAL_NAME.SYSTEM_ID,
                FromRoute: legacyCRMIframePageName,
              })
            },
          },
        ],
      },
    ],
  }
}
